/**
 * BookingWizard - Componente principal del sistema de agendamiento
 * Maneja el flujo completo desde selección de terapeuta hasta confirmación
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { track } from '@vercel/analytics/react';
import { trackMetaEvent } from '../../lib/metaPixel';
import { BookingProvider, useBooking } from './BookingContext';
import PatientVerification from './steps/PatientVerification';
import ServiceTherapistSelector from './steps/ServiceTherapistSelector';
import CalendarSelectorImproved from './steps/CalendarSelectorImproved';
import PatientFormConfirmation from './steps/PatientFormConfirmation';
import ExistingPatientBooking from './ExistingPatientBooking';
import BookingNavigation from './BookingNavigation';
import KonexGuide from './Konex/KonexGuide';
import RoadmapFloatingButton from './Roadmap/RoadmapFloatingButton';
import CoupleRoadmapMap from './Roadmap/CoupleRoadmapMap';
import { useRoadmapProgreso } from './Roadmap/useRoadmapProgreso';
import BookingVoucher, { type BookingVoucherRow } from './BookingVoucher';
import useTerapeutas from '../../hooks/useTerapeutas';
import { getParejaNudgeState, PAREJA_NUDGE_LOCALSTORAGE_KEY } from '../../services/roadmap.service';
import { TherapistExtended } from '../../types';
import { downloadICSEvent, type ICSEventInput } from '../../lib/utils/icsGenerator';
import { buildGoogleCalendarUrl, buildOutlookCalendarUrl } from '../../lib/utils/calendarLinks';
import { CLINIC_OFFICE_ADDRESS, CLINIC_BANK_TRANSFER } from '../../data/clinicPaymentInfo';
import {
  migrateLegacyRut,
  loadProgress,
  loadRut,
  saveProgress,
  clearBookingPersistence,
  relativeTimeFromNow,
  type PersistedProgress,
} from './bookingPersistence';
import './BookingWizard.css';

export type BookingStep = 'verification' | 'service-therapist' | 'calendar' | 'patient-confirmation' | 'existing-patient' | 'success';

interface BookingWizardProps {
  initialStep?: BookingStep;
}

const BookingWizard: React.FC<BookingWizardProps> = ({
  initialStep = 'verification'
}) => {
  const [currentStep, setCurrentStep] = useState<BookingStep>(initialStep);
  const [searchParams] = useSearchParams();
  const preSelectedTherapistId = searchParams.get('therapistId') || undefined;
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [completedBookingId, setCompletedBookingId] = useState<string>('');
  const [completedMeetLink,  setCompletedMeetLink]  = useState<string | undefined>(undefined);

  // FUNC-125: el mapa de la ruta de pareja (y su RPC) recién se habilitan cuando
  // el paciente pasó el gate de contacto de ExistingPatientBooking — antes, con
  // solo tipear un RUT, el botón flotante aparecía encima del gate y exponía el
  // progreso de pareja de ese RUT. Se resetea al volver a 'verification' (cambio
  // de paciente).
  // FUNC-126: el gate de contacto ya no existe (entra solo con RUT) y
  // ExistingPatientBooking confirma al montar. NO resetear al entrar a
  // 'existing-patient': el efecto del padre corre después del del hijo en el
  // mismo commit y pisaría esa confirmación.
  const [contactConfirmed, setContactConfirmed] = useState(false);
  useEffect(() => {
    if (currentStep === 'verification') {
      setContactConfirmed(false);
    }
  }, [currentStep]);

  // ✅ FLUJO CORREGIDO: 3 PASOS DESPUÉS DEL RUT 
  const steps: { id: BookingStep; title: string; icon: string }[] = [
    { id: 'service-therapist', title: 'Configurar consulta', icon: 'fas fa-stethoscope' },
    { id: 'calendar', title: 'Fecha y Hora', icon: 'fas fa-calendar-alt' },
    { id: 'patient-confirmation', title: 'Datos y Confirmación', icon: 'fas fa-check-circle' }
  ];

  const getCurrentStepIndex = () => {
    return steps.findIndex(step => step.id === currentStep);
  };

  const goToStep = (step: BookingStep) => {
    const currentIndex = getCurrentStepIndex();
    const targetIndex = steps.findIndex(s => s.id === step);
    setDirection(targetIndex > currentIndex ? 'forward' : 'backward');
    setCurrentStep(step);
  };

  const goToNextStep = () => {
    const currentIndex = getCurrentStepIndex();
    if (currentIndex < steps.length - 1) {
      setDirection('forward');
      setCurrentStep(steps[currentIndex + 1].id);
    }
  };

  const goToPreviousStep = () => {
    const currentIndex = getCurrentStepIndex();
    if (currentIndex > 0) {
      setDirection('backward');
      setCurrentStep(steps[currentIndex - 1].id);
    }
  };

  // Funnel de conversión — solo nombre de paso, nunca tipo de terapia/motivo/datos del paciente (dato de salud sensible)
  useEffect(() => {
    track('booking_step_view', { step: currentStep });
    // Meta Pixel: reserva ya creada (paso success) — sin parámetros.
    if (currentStep === 'success') trackMetaEvent('Schedule');
  }, [currentStep]);

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'verification':
        return <PatientVerification 
          onNext={goToNextStep} 
          onExistingPatient={() => setCurrentStep('existing-patient')}
        />;
      case 'existing-patient':
        return (
          <ExistingPatientBooking
            onComplete={(bookingId?: string, meetLink?: string) => {
              setCompletedBookingId(bookingId || '');
              setCompletedMeetLink(meetLink);
              clearBookingPersistence(); // P1-5: reserva confirmada, no dejar rastro en el dispositivo
              track('booking_completado', { patient_type: 'existente' });
              setCurrentStep('success');
            }}
            onBack={() => setCurrentStep('verification')}
            onContactConfirmed={() => setContactConfirmed(true)}
          />
        );
      case 'service-therapist':
        return (
          <ServiceTherapistSelector
            onNext={goToNextStep}
            onPrevious={goToPreviousStep}
            preSelectedTherapistId={preSelectedTherapistId}
          />
        );
      case 'calendar':
        return (
          <CalendarSelectorImproved 
            onNext={goToNextStep} 
            onPrevious={goToPreviousStep} 
          />
        );
      case 'patient-confirmation':
        return (
          <PatientFormConfirmation
            onPrevious={goToPreviousStep}
            onComplete={(bookingId: string, meetLink?: string) => {
              setCompletedBookingId(bookingId);
              setCompletedMeetLink(meetLink);
              clearBookingPersistence(); // P1-5: reserva confirmada, no dejar rastro en el dispositivo
              track('booking_completado', { patient_type: 'nuevo' });
              setDirection('forward');
              setCurrentStep('success');
            }}
          />
        );
      case 'success':
        return (
          <BookingSuccess
            bookingId={completedBookingId}
            meetLink={completedMeetLink}
            onBookAgain={(isExisting: boolean) => {
              setDirection('backward');
              setCurrentStep(isExisting ? 'existing-patient' : 'service-therapist');
            }}
          />
        );
      default:
        return <PatientVerification onNext={goToNextStep} />;
    }
  };

  return (
    <BookingProvider>
      <BookingWizardContent 
        steps={steps}
        currentStep={currentStep}
        currentStepIndex={getCurrentStepIndex()}
        onStepClick={goToStep}
        renderCurrentStep={renderCurrentStep}
        contactConfirmed={contactConfirmed}
      />
    </BookingProvider>
  );
};

// ── P1-5: persistencia de progreso ───────────────────────────────────

// El paso guardado puede quedar por delante de lo que el contexto pudo rehidratar
// (ej: terapeuta ya no existe, o el paciente recargó sin haber elegido horario).
// Solo hace clamp HACIA ATRÁS — nunca adelanta al paciente más allá de donde estaba.
const RESUME_ORDER: BookingStep[] = ['service-therapist', 'calendar', 'patient-confirmation'];
const safeResumeStep = (p: PersistedProgress): BookingStep => {
  let target = p.step as BookingStep;
  const at = (s: BookingStep) => RESUME_ORDER.indexOf(s);
  if (!p.timeSlot && at(target) > at('calendar')) target = 'calendar';
  if ((!p.service || !p.therapistId) && at(target) > at('service-therapist')) target = 'service-therapist';
  return target;
};

// Banner NO intrusivo en el paso del RUT. Nunca previsualiza valores del progreso
// (nombre/motivo/dirección) — en un dispositivo compartido sería fuga de confidencialidad.
const BookingResumeBanner: React.FC<{
  savedAt: number;
  onResume: () => void;
  onDiscard: () => void;
}> = ({ savedAt, onResume, onDiscard }) => (
  <motion.section
    className="booking-resume-banner"
    aria-label="Reserva sin terminar"
    initial={{ opacity: 0, y: -8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
  >
    <div className="booking-resume-banner__info">
      <i className="fas fa-clock-rotate-left" aria-hidden="true" />
      <div>
        <p className="booking-resume-banner__title">Tenés una reserva sin terminar</p>
        <p className="booking-resume-banner__meta">
          La empezaste {relativeTimeFromNow(savedAt)} en este dispositivo.
        </p>
      </div>
    </div>
    <div className="booking-resume-banner__actions">
      <button
        type="button"
        className="booking-resume-banner__btn booking-resume-banner__btn--primary"
        onClick={onResume}
      >
        Retomar
      </button>
      <button
        type="button"
        className="booking-resume-banner__btn booking-resume-banner__btn--ghost"
        onClick={onDiscard}
      >
        Empezar de nuevo
      </button>
    </div>
  </motion.section>
);

// Rehidrata el objeto terapeuta a partir del `therapistId` guardado (el objeto entero
// nunca se persiste). One-shot: resuelve una vez y el padre lo desmonta.
const TherapistRehydrator: React.FC<{
  therapistId: string;
  onResolved: (t: TherapistExtended | null) => void;
}> = ({ therapistId, onResolved }) => {
  const { terapeutasExtended, loading, error } = useTerapeutas();
  const settled = useRef(false);

  useEffect(() => {
    if (settled.current || loading) return;
    settled.current = true;
    if (error) {
      onResolved(null);
      return;
    }
    onResolved(terapeutasExtended.find((t) => t.id === therapistId) ?? null);
  }, [loading, error, terapeutasExtended, therapistId, onResolved]);

  return (
    <div className="booking-resume-loading" role="status" aria-live="polite">
      <span className="booking-resume-loading__pulse" aria-hidden="true" />
      <p>Recuperando tu reserva…</p>
    </div>
  );
};

// Componente interno que maneja la lógica de navegación
interface BookingWizardContentProps {
  steps: { id: BookingStep; title: string; icon: string }[];
  currentStep: BookingStep;
  currentStepIndex: number;
  onStepClick: (step: BookingStep) => void;
  renderCurrentStep: () => React.ReactNode;
  contactConfirmed: boolean;
}

const BookingWizardContent: React.FC<BookingWizardContentProps> = ({
  steps,
  currentStep,
  currentStepIndex,
  onStepClick,
  renderCurrentStep,
  contactConfirmed
}) => {
  const {
    state,
    setPatientVerification,
    setService,
    setTherapist,
    setTimeSlot,
    setMetodoPagoDeclarado,
    setParejaNudge,
  } = useBooking();

  // Roadmap Feature 1 — nudge de nombre_pareja (docs/plan-ruta-terapeutica-pareja.md §6.3).
  // Única llamada a get_pareja_nudge_state del flujo: vive acá (no en ExistingPatientBooking
  // ni en Konex) para que ambos consumidores lean el mismo resultado desde BookingContext
  // en vez de duplicar el RPC. Se dispara al entrar a 'existing-patient' con paciente
  // identificado; `konex_pareja_nudge='done'` en localStorage evita el round-trip para
  // siempre en este dispositivo una vez que el paciente guardó el nombre.
  const patientForNudge = state.patientVerification?.existingPatient;
  const patientIdForNudge = patientForNudge?.id;
  const patientRutForNudge = patientForNudge?.rut;
  useEffect(() => {
    if (currentStep !== 'existing-patient' || !patientIdForNudge || !patientRutForNudge) return;

    try {
      if (window.localStorage.getItem(PAREJA_NUDGE_LOCALSTORAGE_KEY) === 'done') {
        setParejaNudge(false);
        return;
      }
    } catch {
      /* storage deshabilitado (incógnito) — sigue al RPC */
    }

    let cancelled = false;
    setParejaNudge(null); // estado "cargando" mientras se resuelve para esta visita
    getParejaNudgeState(patientIdForNudge, patientRutForNudge).then((result) => {
      if (!cancelled) setParejaNudge(result);
    });
    return () => {
      cancelled = true;
    };
  }, [currentStep, patientIdForNudge, patientRutForNudge, setParejaNudge]);

  // Roadmap Feature 2 — mapa de 12 estaciones (docs/plan-ruta-terapeutica-pareja.md
  // §7.5/§7.6, docs/spec_roadmap_pareja_map.md). Botón flotante + overlay,
  // hermanos de <KonexGuide />. `isRoadmapOpen` controla el `inert` del resto
  // del wizard mientras el mapa (modal real en ambos breakpoints, spec §6.2)
  // está abierto — el foco vuelve al botón flotante al cerrar (spec §11).
  const [isRoadmapOpen, setIsRoadmapOpen] = useState(false);
  const roadmapFabRef = useRef<HTMLButtonElement>(null);
  // Refetch al abrir el mapa y al llegar al éxito — una consulta de pareja
  // recién agendada desbloquea la estación siguiente sin recargar.
  const [roadmapRefreshKey, setRoadmapRefreshKey] = useState(0);
  const roadmap = useRoadmapProgreso(contactConfirmed, roadmapRefreshKey);
  useEffect(() => {
    if (currentStep === 'success') setRoadmapRefreshKey((k) => k + 1);
  }, [currentStep]);

  // Progreso persistido — se lee UNA vez al montar. La rehidratación NO es automática:
  // el contexto sigue vacío hasta que el paciente elige "Retomar" (control explícito).
  const [resume] = useState(() => {
    migrateLegacyRut();
    const progress = loadProgress();
    return progress ? { progress, rut: loadRut() } : null;
  });
  const [resumeDone, setResumeDone] = useState(false);
  const [rehydratingId, setRehydratingId] = useState<string | null>(null);

  const handleResume = useCallback(() => {
    if (!resume) return;
    const { progress, rut } = resume;
    if (rut?.rut) {
      setPatientVerification({
        rut: rut.rut,
        isExisting: false,
        existingPatient: null,
        isExtranjero: rut.isExtranjero,
      });
    }
    if (progress.service) setService(progress.service);
    if (progress.timeSlot) setTimeSlot(progress.timeSlot);
    setMetodoPagoDeclarado(progress.metodoPago);

    if (progress.therapistId) {
      setRehydratingId(progress.therapistId); // <TherapistRehydrator> completa el salto de paso
    } else {
      setResumeDone(true);
      onStepClick('service-therapist');
    }
  }, [resume, setPatientVerification, setService, setTimeSlot, setMetodoPagoDeclarado, onStepClick]);

  const handleTherapistRehydrated = useCallback(
    (t: TherapistExtended | null) => {
      const target = t && resume ? safeResumeStep(resume.progress) : 'service-therapist';
      if (t) setTherapist(t);
      setRehydratingId(null);
      setResumeDone(true);
      onStepClick(target);
    },
    [resume, setTherapist, onStepClick]
  );

  const handleDiscardResume = useCallback(() => {
    clearBookingPersistence();
    setResumeDone(true);
  }, []);

  // Escritura del blob de progreso en cada cambio de paso / selección.
  // `saveProgress` no-op en pasos no resumibles (verification / existing-patient / success).
  const selectedTherapistId = state.selectedTherapist?.id ?? null;
  useEffect(() => {
    if (rehydratingId) return; // mid-resume: no escribir un blob a medias
    saveProgress({
      step: currentStep,
      therapistId: selectedTherapistId,
      service: state.selectedService,
      timeSlot: state.selectedTimeSlot,
      metodoPago: state.metodoPagoDeclarado,
    });
  }, [
    currentStep,
    rehydratingId,
    selectedTherapistId,
    state.selectedService,
    state.selectedTimeSlot,
    state.metodoPagoDeclarado,
  ]);

  // Backstop: si se llega a `success` por cualquier camino, no dejar rastro.
  useEffect(() => {
    if (currentStep === 'success') clearBookingPersistence();
  }, [currentStep]);

  // Determinar si mostrar la navegación
  // Ocultar para RUT no verificado Y para flujo de paciente existente optimizado
  const showNavigation =
    state.patientVerification !== null &&
    currentStep !== 'existing-patient' &&
    currentStep !== 'success' &&
    !rehydratingId;

  const showResumeBanner =
    !!resume && !resumeDone && !rehydratingId && currentStep === 'verification';

  // Sin esto el scroll queda donde estaba en el paso anterior (ej: paciente scrolleó
  // para ver terapeutas) y el paso nuevo entra fuera de vista — el calendario del
  // paso 2 quedaba arriba del fold en mobile obligando a scrollear hacia arriba.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  return (
    <div className={`booking-wizard ${!showNavigation ? 'rut-capture-mode' : ''}`}>
      {/* Envoltorio inert-able (spec_roadmap_pareja_map.md §6.2/§11): mientras el
          mapa de la ruta de pareja está abierto, todo esto queda fuera de foco/
          lectura de pantalla sin tocar KonexGuide.tsx ni la navegación del wizard. */}
      <div inert={isRoadmapOpen || undefined}>
        {showNavigation && (
          /* Header unificado: brand + stepper en una sola barra sticky */
          <div className="booking-header-unified">
            <span className="booking-brand">
              <i className="fas fa-calendar-plus" aria-hidden="true"></i>
              Reservar Cita
            </span>

            <BookingNavigation
              steps={steps}
              currentStep={currentStep}
              currentStepIndex={currentStepIndex}
              onStepClick={onStepClick}
            />
          </div>
        )}

        {/* Contenido del paso actual */}
        <div className="booking-content">
          <div className="container">
            <div className="step-container">
              {showResumeBanner && resume && (
                <BookingResumeBanner
                  savedAt={resume.progress.savedAt}
                  onResume={handleResume}
                  onDiscard={handleDiscardResume}
                />
              )}

              {rehydratingId ? (
                <TherapistRehydrator
                  therapistId={rehydratingId}
                  onResolved={handleTherapistRehydrated}
                />
              ) : (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{
                      opacity: 0,
                      x: 50,
                      scale: 0.95
                    }}
                    animate={{
                      opacity: 1,
                      x: 0,
                      scale: 1
                    }}
                    exit={{
                      opacity: 0,
                      x: -50,
                      scale: 0.95
                    }}
                    transition={{
                      duration: 0.3,
                      ease: [0.4, 0, 0.2, 1]
                    }}
                    className="step-transition-wrapper"
                  >
                    {renderCurrentStep()}
                  </motion.div>
                </AnimatePresence>
              )}
            </div>
          </div>
        </div>

        {/* Konex — guía mascota guionada (Fase A). Hermano de .booking-content,
            dentro de <BookingProvider>. NO participa de la navegación del wizard. */}
        <KonexGuide
          currentStep={currentStep}
          isExisting={state.patientVerification?.isExisting ?? false}
          parejaNudge={state.parejaNudge}
        />
      </div>

      {/* Roadmap Feature 2 — botón flotante + mapa de la ruta de pareja. Hermanos
          de <KonexGuide />, fuera del wrapper inert-able (spec §11). Solo se
          monta con progreso real (paciente identificado + ≥1 consulta pareja
          u override) — modo preview para paciente nuevo descartado a pedido
          del director (2026-09-14): sin historial, la trayectoria no aplica. */}
      {roadmap.hasProgressData && (
        <>
          <RoadmapFloatingButton
            ref={roadmapFabRef}
            currentStep={currentStep}
            isOpen={isRoadmapOpen}
            sesionActual={roadmap.estacionActual}
            total={roadmap.total}
            onOpen={() => {
              setRoadmapRefreshKey((k) => k + 1);
              setIsRoadmapOpen(true);
            }}
          />
          <CoupleRoadmapMap
            open={isRoadmapOpen}
            ready={!roadmap.loading}
            completadas={roadmap.completadas}
            estacionActual={roadmap.estacionActual}
            total={roadmap.total}
            fechaProximaConsulta={roadmap.fechaProximaConsulta}
            patientId={roadmap.patientId}
            onClose={() => setIsRoadmapOpen(false)}
            returnFocusRef={roadmapFabRef}
          />
        </>
      )}
    </div>
  );
};

// Pantalla de éxito post-booking — se monta en el wizard para que el contexto siga disponible.
// UX_VISION P1-8: el resumen es autosuficiente (dónde, cómo pagar, link de la sesión) para
// que el correo sea redundante, no indispensable. Jerarquía de CTAs: primaria = agregar al
// calendario · secundaria = agendar otra · links = volver / contactar.
const BookingSuccess: React.FC<{ bookingId: string; meetLink?: string; onBookAgain: (isExisting: boolean) => void }> = ({ bookingId, meetLink, onBookAgain }) => {
  const navigate = useNavigate();
  const { state, dispatch } = useBooking();
  const [calMenuOpen, setCalMenuOpen] = useState(false);
  const calMenuRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Foco al encabezado al montar — el lector de pantalla arranca desde la
  // confirmación, no desde el final del wizard anterior.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const { selectedTherapist, selectedService, selectedTimeSlot, metodoPagoDeclarado } = state;
  const modality = selectedService?.modality;
  const isOnline = modality === 'online';
  const isDomicilio = modality === 'domicilio';
  const isTransferencia = metodoPagoDeclarado === 'transferencia';
  // RUT/documento que el paciente registró — la boleta se emite a ese identificador por defecto.
  const patientRut = state.patientVerification?.rut;

  // Cierra el menú de calendario al clickear afuera o con Escape.
  useEffect(() => {
    if (!calMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (calMenuRef.current && !calMenuRef.current.contains(e.target as Node)) {
        setCalMenuOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCalMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [calMenuOpen]);

  const handleBookAgain = () => {
    const isExisting = state.patientVerification?.isExisting ?? false;
    dispatch({ type: 'RESET_BOOKING_KEEP_PATIENT' });
    onBookAgain(isExisting);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString + 'T12:00:00-03:00').toLocaleDateString('es-CL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Payload del evento — se comparte entre el .ics y los links de Google/Outlook.
  // `null` si falta cualquier dato: en ese caso el botón queda deshabilitado.
  const calendarEvent: ICSEventInput | null =
    selectedTherapist && selectedService && selectedTimeSlot
      ? {
          uid: bookingId || `CC-${selectedTimeSlot.date}-${selectedTimeSlot.time}`,
          title: `Consulta con ${selectedTherapist.name} — Conectemos Chile`,
          description: [
            `Consulta ${selectedService.name} con ${selectedTherapist.name}.`,
            isOnline && meetLink ? `Enlace de la sesión: ${meetLink}` : null,
          ]
            .filter(Boolean)
            .join('\n'),
          location: isOnline
            ? meetLink || 'Sesión online'
            : isDomicilio
            ? 'En tu domicilio'
            : CLINIC_OFFICE_ADDRESS,
          date: selectedTimeSlot.date,
          time: selectedTimeSlot.time,
          durationMinutes: selectedService.duration,
        }
      : null;

  const handleDownloadICS = () => {
    if (calendarEvent) downloadICSEvent(calendarEvent);
    setCalMenuOpen(false);
  };

  // Filas del comprobante — mismas fuentes que la tarjeta de resumen anterior.
  const voucherDonde = isOnline
    ? 'Sesión online'
    : isDomicilio
    ? 'En tu domicilio'
    : CLINIC_OFFICE_ADDRESS;

  const voucherRows = useMemo<BookingVoucherRow[]>(() => {
    const rows: BookingVoucherRow[] = [];
    if (bookingId) rows.push({ label: 'N° de reserva', value: bookingId, mono: true });
    if (selectedTherapist) rows.push({ label: 'Terapeuta', value: selectedTherapist.name });
    if (selectedTimeSlot) rows.push({ label: 'Fecha', value: formatDate(selectedTimeSlot.date) });
    if (selectedTimeSlot) rows.push({ label: 'Hora', value: `${selectedTimeSlot.time} hrs` });
    if (selectedService)
      rows.push({ label: 'Servicio', value: `${selectedService.name} · ${selectedService.duration} min` });
    if (modality) rows.push({ label: 'Dónde', value: voucherDonde });
    if (selectedService)
      rows.push({ label: 'Valor', value: `$${selectedService.price.toLocaleString('es-CL')}` });
    if (metodoPagoDeclarado)
      rows.push({ label: 'Pago', value: isTransferencia ? 'Transferencia / Efectivo' : isOnline ? 'Débito / Crédito' : 'Débito / Crédito (en la clínica)' });
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, selectedTherapist, selectedTimeSlot, selectedService, modality, metodoPagoDeclarado, isTransferencia, voucherDonde]);

  const emittedLabel = useMemo(() => {
    const now = new Date();
    const fecha = now
      .toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })
      .replace(/\./g, '');
    const hora = now.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `Emitido el ${fecha}, ${hora}`;
  }, []);

  const announcement = [
    'Cita confirmada.',
    selectedTherapist?.name ? `${selectedTherapist.name}.` : null,
    selectedTimeSlot ? `${formatDate(selectedTimeSlot.date)} a las ${selectedTimeSlot.time} hrs.` : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <motion.div
      className="booking-success-screen"
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
    >
      <div className="success-icon-wrapper">
        <motion.div
          className="success-checkmark"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 300, damping: 20 }}
        >
          <i className="fas fa-check"></i>
        </motion.div>
      </div>

      <h1 className="success-heading" ref={headingRef} tabIndex={-1}>
        ¡Cita confirmada!
      </h1>
      <p className="success-lead">Gracias por confiar en Conectemos Chile.</p>

      <BookingVoucher rows={voucherRows} emittedLabel={emittedLabel} announcement={announcement} />

      <div className="success-divider" role="presentation" />
      <p className="success-keep-note">
        <i className="fas fa-receipt" aria-hidden="true"></i>
        Guardá este comprobante de tu reserva. <strong>No es una boleta ni un comprobante de pago.</strong> Abajo están los datos para pagar y el acceso a tu sesión.
      </p>

      {isOnline && (
        <div className="success-block success-block--online">
          <p className="success-block__label">
            <i className="fas fa-video" aria-hidden="true"></i>
            Acceso a tu sesión online
          </p>
          {meetLink ? (
            <>
              <a href={meetLink} target="_blank" rel="noreferrer" className="success-block__link">
                {meetLink}
              </a>
              <p className="success-block__hint">
                Este enlace también te llega por correo, dentro del <strong>detalle de tu consulta</strong>.
              </p>
            </>
          ) : (
            <p className="success-block__text">
              El enlace de tu videollamada te llega por correo, dentro del <strong>detalle de tu consulta</strong>.
            </p>
          )}
        </div>
      )}

      {isTransferencia && (
        <div className="success-block success-block--pay">
          <p className="success-block__label">
            <i className="fas fa-building-columns" aria-hidden="true"></i>
            Datos para transferir
          </p>
          <dl className="success-paydata">
            <div><dt>Titular</dt><dd>{CLINIC_BANK_TRANSFER.titular}</dd></div>
            <div><dt>RUT</dt><dd>{CLINIC_BANK_TRANSFER.rut}</dd></div>
            <div><dt>Banco</dt><dd>{CLINIC_BANK_TRANSFER.banco}</dd></div>
            <div><dt>Tipo de cuenta</dt><dd>{CLINIC_BANK_TRANSFER.tipoCuenta}</dd></div>
            <div><dt>N° de cuenta</dt><dd className="success-paydata__mono">{CLINIC_BANK_TRANSFER.numeroCuenta}</dd></div>
          </dl>
          <p className="success-block__hint">
            {isOnline ? 'Envía el comprobante de la transferencia a' : 'Si prefieres pagar en efectivo, puedes hacerlo en la clínica. Si transfieres, envía el comprobante a'}{' '}
            <a href={`mailto:${CLINIC_BANK_TRANSFER.comprobanteEmail}`}>{CLINIC_BANK_TRANSFER.comprobanteEmail}</a>{' '}
            o al <a href="https://wa.me/56978009119">{CLINIC_BANK_TRANSFER.comprobanteWhatsapp}</a>, al menos 24 h antes de tu sesión.
          </p>
        </div>
      )}

      {!isTransferencia && (
        <p className="success-email-note">
          <i className="fas fa-credit-card"></i>
          {isOnline
            ? 'Te contactaremos para coordinar el pago con tarjeta de tu sesión online, o puedes pagar por transferencia antes de la sesión.'
            : 'Paga en la clínica con débito o crédito, el día de tu sesión o antes.'}
        </p>
      )}

      <div className="success-block success-block--boleta">
        <p className="success-block__label">
          <i className="fas fa-file-invoice" aria-hidden="true"></i>
          Sobre tu boleta
        </p>
        <p className="success-block__text">
          Cuando pagues tu consulta, la boleta se emite a nombre del RUT que registraste como paciente
          {patientRut ? <> (<strong>{patientRut}</strong>)</> : null}. ¿Necesitás que salga a otro RUT —
          una empresa u otra persona? Avisanos <strong>antes de tu sesión</strong> a{' '}
          <a href="mailto:contacto@conectemoschile.cl">contacto@conectemoschile.cl</a> o al{' '}
          <a href="https://wa.me/56978009119">+56 9 7800 9119</a>.
        </p>
      </div>

      <p className="success-email-note success-email-note--soft">
        <i className="fas fa-envelope"></i>
        Te enviamos una copia de todo esto a tu correo.
      </p>

      {(selectedTherapist?.professionalInfo?.registroSuperintendencia || selectedTherapist?.professionalInfo?.licenseNumber) && (
        <div className="success-block success-block--registry">
          <p className="success-block__label">
            <i className="fas fa-id-card" aria-hidden="true"></i>
            Registro profesional de {selectedTherapist?.name}
          </p>
          <dl className="success-registry-rows">
            {selectedTherapist?.professionalInfo?.registroSuperintendencia && (
              <div>
                <dt>Superintendencia de Salud</dt>
                <dd>{selectedTherapist.professionalInfo.registroSuperintendencia}</dd>
              </div>
            )}
            {selectedTherapist?.professionalInfo?.licenseNumber && (
              <div>
                <dt>Colegio de Psicólogos de Chile</dt>
                <dd>{selectedTherapist.professionalInfo.licenseNumber}</dd>
              </div>
            )}
          </dl>
          <a
            className="success-block__hint-link"
            href="https://rnpi.superdesalud.gob.cl/"
            target="_blank"
            rel="noreferrer"
          >
            Verificar en el Registro Nacional de Prestadores Individuales (RNPI)
          </a>
        </div>
      )}

      <div className="success-actions">
        <div className="success-cal-menu" ref={calMenuRef}>
          <button
            type="button"
            className="btn-success-cta btn-success-cta--primary"
            onClick={() => setCalMenuOpen(open => !open)}
            disabled={!calendarEvent}
            aria-haspopup="menu"
            aria-expanded={calMenuOpen}
          >
            <i className="fas fa-calendar-check" aria-hidden="true"></i>
            Agregar a mi calendario
            <i
              className={`fas fa-chevron-${calMenuOpen ? 'up' : 'down'} success-cal-menu__caret`}
              aria-hidden="true"
            ></i>
          </button>

          {calMenuOpen && calendarEvent && (
            <div className="success-cal-menu__list" role="menu">
              <a
                className="success-cal-menu__item"
                role="menuitem"
                href={buildGoogleCalendarUrl(calendarEvent)}
                target="_blank"
                rel="noreferrer"
                onClick={() => setCalMenuOpen(false)}
              >
                <i className="fab fa-google" aria-hidden="true"></i>
                Google Calendar
              </a>
              <a
                className="success-cal-menu__item"
                role="menuitem"
                href={buildOutlookCalendarUrl(calendarEvent)}
                target="_blank"
                rel="noreferrer"
                onClick={() => setCalMenuOpen(false)}
              >
                <i className="fab fa-microsoft" aria-hidden="true"></i>
                Outlook
              </a>
              <button
                type="button"
                className="success-cal-menu__item"
                role="menuitem"
                onClick={handleDownloadICS}
              >
                <i className="fas fa-download" aria-hidden="true"></i>
                Apple Calendar u otro (.ics)
              </button>
            </div>
          )}
        </div>
        <button
          className="btn-success-cta btn-success-cta--secondary"
          onClick={handleBookAgain}
        >
          <i className="fas fa-calendar-plus"></i>
          Agendar otra hora
        </button>
        <div className="success-actions__links">
          <button type="button" className="success-link" onClick={() => navigate('/')}>
            Volver al inicio
          </button>
          <span aria-hidden="true">·</span>
          <a href="tel:+56978009119" className="success-link">
            Contactar
          </a>
        </div>
      </div>
    </motion.div>
  );
};

export default BookingWizard;